import type { SseEvent } from '@omni/shared';
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { API_BASE, UNAUTHENTICATED_EVENT } from '../api/client';
import { EventBatcher } from './batcher';
import type { SseFrame } from './sseParser';
import { SseStreamClient } from './streamClient';
import {
  initialStreamState,
  streamReducer,
  type StreamAction,
  type StreamState,
} from './streamReducer';

/**
 * The single `/api/stream` subscription for the tab (CONTRACTS §5, R5).
 *
 * - every frame is queued through `EventBatcher`, so React re-renders at most
 *   16 times a second no matter how fast text deltas arrive;
 * - `visibilitychange → visible` after more than 30 s hidden forces a
 *   reconnect carrying `Last-Event-ID` (iOS standalone silently kills the
 *   socket on backgrounding and never tells the page);
 * - the stream is never closed on `turn.finished`: trailing events are legal.
 *
 * Two lifetime rules the socket effect exists to enforce:
 *
 *  1. **The batcher lives and dies with the socket.** React 18 StrictMode
 *     simulates mount → unmount → remount in development, so anything created
 *     by `useMemo` survives a cleanup that disposed it. A batcher owned that
 *     way comes back `disposed` and drops every frame for the rest of the
 *     session — an open connection rendering nothing, only under `pnpm dev`.
 *     Creating it inside the effect means each set-up gets a live one.
 *  2. **Disabling the stream empties it.** `enabled` goes false exactly when
 *     the tab lands on /login — logout, or any 401 (`UNAUTHENTICATED_EVENT` →
 *     `AppRoutes` navigates). Transcripts, tool inputs, approval payloads and
 *     notices of the user who just left must not still be in memory when the
 *     next user signs in on the same device: the server 404s their thread ids,
 *     but a retained reducer state would render the rows anyway.
 */

const HIDDEN_RECONNECT_MS = 30_000;

export interface UseEventSourceResult {
  readonly state: StreamState;
  readonly dispatch: React.Dispatch<StreamAction>;
  readonly reconnect: () => void;
}

function parseFrame(frame: SseFrame): SseEvent | null {
  try {
    const data: unknown = JSON.parse(frame.data);
    return { event: frame.event, data } as SseEvent;
  } catch {
    return null;
  }
}

export function useEventSource(enabled: boolean): UseEventSourceResult {
  const [state, dispatch] = useReducer(streamReducer, initialStreamState);
  const clientRef = useRef<SseStreamClient | null>(null);
  const batcherRef = useRef<EventBatcher<SseEvent> | null>(null);

  useEffect(() => {
    if (!enabled) {
      // The previous effect's cleanup already stopped the socket; what is left
      // is the state it accumulated, which belongs to a session that has ended.
      dispatch({ type: 'reset' });
      return;
    }

    const batcher = new EventBatcher<SseEvent>((events) => {
      dispatch({ type: 'events', events });
    });
    batcherRef.current = batcher;

    const client = new SseStreamClient({
      url: `${API_BASE}/stream`,
      onFrame: (frame) => {
        const event = parseFrame(frame);
        if (event) batcher.push(event);
      },
      onStatus: (status) => dispatch({ type: 'connection', status }),
      onUnauthenticated: () => window.dispatchEvent(new CustomEvent(UNAUTHENTICATED_EVENT)),
    });
    clientRef.current = client;
    client.start();

    return () => {
      client.stop();
      batcher.dispose();
      if (clientRef.current === client) clientRef.current = null;
      if (batcherRef.current === batcher) batcherRef.current = null;
    };
  }, [enabled]);

  const reconnect = useCallback(() => {
    clientRef.current?.reconnectNow();
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let hiddenSince: number | null = null;

    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') {
        hiddenSince = Date.now();
        // Nothing will paint while hidden; land what is queued so the state is
        // whole if the tab is killed.
        batcherRef.current?.flushNow();
        return;
      }
      const away = hiddenSince === null ? 0 : Date.now() - hiddenSince;
      hiddenSince = null;
      if (away > HIDDEN_RECONNECT_MS) clientRef.current?.reconnectNow();
    };

    const onOnline = (): void => clientRef.current?.reconnectNow();
    const onOffline = (): void => dispatch({ type: 'connection', status: 'offline' });

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [enabled]);

  return { state, dispatch, reconnect };
}
